import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Send, CheckCircle, AlertCircle, User, Mail, FileText, MessageSquare } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const inputStyle = {
  width: '100%', borderRadius: 10,
  background: '#FAF8F5', border: '1px solid #ECE7E1',
  paddingLeft: 40, paddingRight: 16, fontSize: 14,
  color: '#1A2B3C', outline: 'none',
};

export default function IletisimPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState(null); // null | 'success' | 'error'
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    setLoading(true);
    try {
      await axios.post(`${API}/api/contact`, form);
      setStatus('success');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #15263A 0%, #1E3348 80px, #F8F6F3 80px, #F8F6F3 100%)' }}>
      <Helmet>
        <title>İletişim — DoktorCebinde</title>
        <meta name="description" content="DoktorCebinde ile iletişime geçin. Sorularınız, önerileriniz veya geri bildirimleriniz için form doldurun." />
      </Helmet>
      <Header />

      <section style={{ maxWidth: 560, margin: '0 auto', padding: '48px 24px 80px' }}>
        {/* Title */}
        <div className="text-center" style={{ marginBottom: 36 }}>
          <h1 style={{ fontSize: 32, fontWeight: 700, color: '#1A2B3C', marginBottom: 12 }} data-testid="iletisim-title">
            İletişim
          </h1>
          <p style={{ fontSize: 15, color: '#687482', lineHeight: 1.6, maxWidth: 420, margin: '0 auto' }}>
            Sorularınız, önerileriniz veya geri bildirimleriniz için bizimle iletişime geçebilirsiniz.
          </p>
        </div>

        {/* Status messages */}
        {status === 'success' && (
          <div className="flex items-start gap-3 p-4 mb-6" style={{ background: 'rgba(90,158,122,0.08)', border: '1px solid rgba(90,158,122,0.2)', borderRadius: 12 }} data-testid="iletisim-success">
            <CheckCircle size={18} style={{ color: '#5A9E7A', flexShrink: 0, marginTop: 1 }} />
            <p style={{ fontSize: 14, color: '#5A9E7A', margin: 0, lineHeight: 1.5 }}>
              Mesajınız başarıyla gönderildi. En kısa sürede size dönüş yapılacaktır.
            </p>
          </div>
        )}
        {status === 'error' && (
          <div className="flex items-start gap-3 p-4 mb-6" style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: 12 }} data-testid="iletisim-error">
            <AlertCircle size={18} style={{ color: '#DC2626', flexShrink: 0, marginTop: 1 }} />
            <p style={{ fontSize: 14, color: '#DC2626', margin: 0, lineHeight: 1.5 }}>
              Mesaj gönderilirken bir sorun oluştu. Lütfen daha sonra tekrar deneyin.
            </p>
          </div>
        )}

        {/* Form Card */}
        <div style={{ background: '#fff', border: '1px solid #ECE7E1', borderRadius: 20, padding: '36px 32px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }} data-testid="iletisim-form-card">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Ad Soyad */}
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#435160', marginBottom: 6 }}>Ad Soyad</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2" style={{ width: 16, height: 16, color: '#94A3B8' }} />
                <input name="name" type="text" value={form.name} onChange={handleChange}
                  style={{ ...inputStyle, height: 46 }}
                  placeholder="Adınızı ve soyadınızı yazın" required data-testid="iletisim-name" />
              </div>
            </div>

            {/* E-posta */}
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#435160', marginBottom: 6 }}>E-posta</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2" style={{ width: 16, height: 16, color: '#94A3B8' }} />
                <input name="email" type="email" value={form.email} onChange={handleChange}
                  style={{ ...inputStyle, height: 46 }}
                  placeholder="E-posta adresinizi yazın" required data-testid="iletisim-email" />
              </div>
            </div>

            {/* Konu */}
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#435160', marginBottom: 6 }}>Konu</label>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 -translate-y-1/2" style={{ width: 16, height: 16, color: '#94A3B8' }} />
                <input name="subject" type="text" value={form.subject} onChange={handleChange}
                  style={{ ...inputStyle, height: 46 }}
                  placeholder="Mesaj konusunu yazın" required data-testid="iletisim-subject" />
              </div>
            </div>

            {/* Mesaj */}
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#435160', marginBottom: 6 }}>Mesaj</label>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-4" style={{ width: 16, height: 16, color: '#94A3B8' }} />
                <textarea name="message" value={form.message} onChange={handleChange}
                  style={{ ...inputStyle, height: 140, paddingTop: 14, resize: 'none' }}
                  placeholder="Mesajınızı buraya yazın" required data-testid="iletisim-message" />
              </div>
            </div>

            {/* Gönder */}
            <button type="submit" disabled={loading} className="w-full disabled:opacity-50"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                background: '#1A2B3C', color: '#fff', borderRadius: 4,
                padding: '14px', fontWeight: 600, fontSize: 15,
                border: 'none', cursor: 'pointer', transition: 'all 0.2s',
              }}
              data-testid="iletisim-submit">
              {loading ? 'Gönderiliyor...' : <><Send size={16} /> Gönder</>}
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
